import { forwardRef, type InputHTMLAttributes, useId } from 'react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  /** Validation or hint message; when `invalid` is set it is announced as an error. */
  message?: string | undefined;
  invalid?: boolean;
}

/** Labelled, accessible checkbox input with error wiring. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, message, invalid = false, required, className, ...rest },
  ref,
) {
  const inputId = useId();
  const messageId = `${inputId}-message`;

  return (
    <div className="zs-field zs-checkbox">
      <div className="zs-checkbox__row">
        <input
          id={inputId}
          type="checkbox"
          ref={ref}
          className={['zs-checkbox__input', className].filter(Boolean).join(' ')}
          aria-invalid={invalid || undefined}
          aria-describedby={message ? messageId : undefined}
          aria-required={required || undefined}
          required={required}
          {...rest}
        />
        <label className="zs-checkbox__label" htmlFor={inputId}>
          {label}
          {required ? <span aria-hidden="true"> *</span> : null}
        </label>
      </div>
      {message ? (
        <span
          id={messageId}
          className={invalid ? 'zs-field__error' : 'zs-field__label'}
          role={invalid ? 'alert' : undefined}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
});
